/**
 * OperatorLoadingSkeleton Component
 *
 * Skeleton loading states for the operator portal. Uses motion-safe:animate-pulse
 * so reduced-motion users see static placeholders instead of pulsing.
 *
 * Variants:
 * - cards (default): section title + 3 card skeletons
 * - card: single card skeleton
 * - briefing: single tall green-tinted skeleton
 * - stats: 2x2 grid of stat skeletons
 * - page: briefing + section title + 3 card skeletons
 */

import { cn } from '../../../lib/utils';

interface OperatorLoadingSkeletonProps {
  /** Skeleton layout variant */
  variant?: 'cards' | 'card' | 'briefing' | 'stats' | 'page';
  /** Additional CSS classes */
  className?: string;
}

function SkeletonBlock({ className }: { className?: string }) {
  return (
    <div
      className={cn('motion-safe:animate-pulse rounded-2xl bg-gray-100', className)}
    />
  );
}

function SectionTitleSkeleton() {
  return (
    <div className="h-5 w-32 motion-safe:animate-pulse rounded-lg bg-gray-200" />
  );
}

function CardsSkeleton() {
  return (
    <div className="space-y-3">
      <SectionTitleSkeleton />
      <SkeletonBlock className="h-24" />
      <SkeletonBlock className="h-24" />
      <SkeletonBlock className="h-24" />
    </div>
  );
}

function BriefingSkeleton() {
  return <SkeletonBlock className="h-48 bg-guild-green/20" />;
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3">
      <SkeletonBlock className="h-20" />
      <SkeletonBlock className="h-20" />
      <SkeletonBlock className="h-20" />
      <SkeletonBlock className="h-20" />
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-4">
      <BriefingSkeleton />
      <div className="space-y-3">
        <SectionTitleSkeleton />
        <SkeletonBlock className="h-24" />
        <SkeletonBlock className="h-24" />
        <SkeletonBlock className="h-24" />
      </div>
    </div>
  );
}

export function OperatorLoadingSkeleton({
  variant = 'cards',
  className,
}: OperatorLoadingSkeletonProps) {
  return (
    <div role="status" aria-label="Loading" className={className}>
      {variant === 'cards' && <CardsSkeleton />}
      {variant === 'card' && <SkeletonBlock className="h-24" />}
      {variant === 'briefing' && <BriefingSkeleton />}
      {variant === 'stats' && <StatsSkeleton />}
      {variant === 'page' && <PageSkeleton />}
      <span className="sr-only">Loading...</span>
    </div>
  );
}
